import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { firestore } from "@/firebaseConfig";

export default {
  namespaced: true,
  state() {
    return {
      items: [],
      loading: false,
    };
  },
  mutations: {
    SET_ITEMS(state, items) {
      state.items = items;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    ADD_ITEM(state, item) {
      state.items.push(item);
    },
    UPDATE_ITEM(state, updatedItem) {
      const index = state.items.findIndex((item) => item.id === updatedItem.id);
      if (index !== -1) {
        state.items.splice(index, 1, updatedItem);
      }
    },
    REMOVE_ITEM(state, id) {
      state.items = state.items.filter((item) => item.id !== id);
    },
  },
  actions: {
    async fetchItems({ commit, rootGetters }) {
      const user = rootGetters.getUser;
      if (!user) return;
      
      commit("SET_LOADING", true);
      try {
        const q = query(
          collection(firestore, "trackers"),
          where("userId", "==", user.uid)
        );
        const querySnapshot = await getDocs(q);
        const items = [];
        querySnapshot.forEach((doc) => {
          items.push({ id: doc.id, ...doc.data() });
        });
        // Sort items by nextDue ascending
        items.sort((a, b) => new Date(a.nextDue) - new Date(b.nextDue));
        commit("SET_ITEMS", items);
      } catch (error) {
        console.error("Error fetching tracker items:", error);
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async addItem({ commit, rootGetters }, item) {
      const user = rootGetters.getUser;
      if (!user) return;
      
      const newItem = { ...item, userId: user.uid };
      try {
        const docRef = await addDoc(collection(firestore, "trackers"), newItem);
        commit("ADD_ITEM", { id: docRef.id, ...newItem });
      } catch (error) {
        console.error("Error adding tracker item:", error);
      }
    },
    async updateItem({ commit }, item) {
      try {
        const itemRef = doc(firestore, "trackers", item.id);
        const itemToUpdate = { ...item };
        delete itemToUpdate.id; // Don't write id to document fields
        await updateDoc(itemRef, itemToUpdate);
        commit("UPDATE_ITEM", item);
      } catch (error) {
        console.error("Error updating tracker item:", error);
      }
    },
    async removeItem({ commit }, id) {
      try {
        await deleteDoc(doc(firestore, "trackers", id));
        commit("REMOVE_ITEM", id);
      } catch (error) {
        console.error("Error removing tracker item:", error);
      }
    },
    async completeItem({ dispatch }, item) {
      const now = new Date();
      const history = item.history ? [...item.history] : [];
      // Log the completion on the scheduled due date so the calendar dot stays on the correct day
      history.push(item.nextDue);

      if (!item.isRecurring) {
        // One-time item completed -> mark as completed instead of deleting
        const updatedItem = {
          ...item,
          isCompleted: true,
          lastCompleted: now.toISOString(),
          history
        };
        await dispatch("updateItem", updatedItem);
        return;
      }
      
      const nextDue = new Date(item.nextDue);
      
      // Calculate the next recurrence based strictly on the current due date so the schedule doesn't drift
      let baseDate = new Date(nextDue);
      
      if (item.recurrenceUnit === 'days') {
        baseDate.setDate(baseDate.getDate() + item.recurrenceValue);
      } else if (item.recurrenceUnit === 'weeks') {
        baseDate.setDate(baseDate.getDate() + item.recurrenceValue * 7);
      } else if (item.recurrenceUnit === 'months') {
        baseDate.setMonth(baseDate.getMonth() + item.recurrenceValue);
      } else if (item.recurrenceUnit === 'years') {
        baseDate.setFullYear(baseDate.getFullYear() + item.recurrenceValue);
      }
      
      const updatedItem = {
        ...item,
        lastCompleted: now.toISOString(),
        nextDue: baseDate.toISOString(),
        history
      };
      
      await dispatch("updateItem", updatedItem);
    },
  },
  getters: {
    getItems(state) {
      return state.items;
    },
    isLoading(state) {
      return state.loading;
    },
  },
};
